/**
 * Geometria pura das anotações manuais da mesa (E36.F3). // ux-neutrality:allow doc-comment
 *
 * Conversões entre o box de TELA do overlay (pós-zoom, `getBoundingClientRect`) // ux-neutrality:allow doc-comment
 * e o espaço NORMALIZADO da imagem contestada ([0,1]; raio = fração da largura). // ux-neutrality:allow doc-comment
 * O box do overlay casa com o do <img> (mesma tática do .contested-markers), // ux-neutrality:allow doc-comment
 * então o letterbox do object-contain é reproduzido aqui: escala uniforme // ux-neutrality:allow doc-comment
 * `min(boxW/W, boxH/H)` + offsets centrados. Como o rect já é pós-transform, // ux-neutrality:allow doc-comment
 * a conversão funciona em QUALQUER zoom do ZoomPanViewport (E36.F2) — zoom // ux-neutrality:allow doc-comment
 * nunca distorce a anotação persistida. // ux-neutrality:allow doc-comment
 */
import type {
  AnnotationGeometryData,
  ArrowGeometryData,
  CircleGeometryData,
} from '@/lib/mesa_annotations';
import { isArrowGeometry } from '@/lib/mesa_annotations';

export interface ImageSize {
  width: number;
  height: number;
}

export interface NormPoint {
  x: number;
  y: number;
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * Ponto de tela → ponto normalizado da imagem, dentro do box do overlay.
 * `null` quando não há layout (jsdom, imagem não carregada) — gesto é ignorado.
 */
export function clientToImageNorm(
  clientX: number,
  clientY: number,
  box: { left: number; top: number; width: number; height: number },
  natural: ImageSize,
): NormPoint | null {
  if (box.width <= 0 || box.height <= 0 || natural.width <= 0 || natural.height <= 0) return null;
  const scale = Math.min(box.width / natural.width, box.height / natural.height);
  if (!Number.isFinite(scale) || scale <= 0) return null;
  const offsetX = (box.width - natural.width * scale) / 2;
  const offsetY = (box.height - natural.height * scale) / 2;
  return {
    x: clamp01((clientX - box.left - offsetX) / scale / natural.width),
    y: clamp01((clientY - box.top - offsetY) / scale / natural.height),
  };
}

/** Seta concluída a partir do arrasto cauda→ponta. `null` se degenerada. */
export function arrowFromDrag(start: NormPoint, end: NormPoint): ArrowGeometryData | null {
  if (start.x === end.x && start.y === end.y) return null;
  return { x1: start.x, y1: start.y, x2: end.x, y2: end.y };
}

/**
 * Círculo concluído a partir do arrasto centro→borda. O raio é a distância em
 * px da imagem convertida para fração da LARGURA (contrato do backend).
 * `null` se degenerado.
 */
export function circleFromDrag(
  start: NormPoint,
  end: NormPoint,
  natural: ImageSize,
): CircleGeometryData | null {
  const dxPx = (end.x - start.x) * natural.width;
  const dyPx = (end.y - start.y) * natural.height;
  const r = Math.min(1, Math.hypot(dxPx, dyPx) / natural.width);
  if (r <= 0) return null;
  return { cx: start.x, cy: start.y, r };
}

/**
 * Translada uma geometria pelo delta normalizado, contendo TODOS os pontos de
 * ancoragem em [0,1] (a anotação nunca escapa da imagem ao ser movida).
 */
export function translateGeometry(
  geometry: AnnotationGeometryData,
  dx: number,
  dy: number,
): AnnotationGeometryData {
  if (isArrowGeometry(geometry)) {
    const boundedDx = Math.min(
      1 - Math.max(geometry.x1, geometry.x2),
      Math.max(-Math.min(geometry.x1, geometry.x2), dx),
    );
    const boundedDy = Math.min(
      1 - Math.max(geometry.y1, geometry.y2),
      Math.max(-Math.min(geometry.y1, geometry.y2), dy),
    );
    return {
      x1: geometry.x1 + boundedDx,
      y1: geometry.y1 + boundedDy,
      x2: geometry.x2 + boundedDx,
      y2: geometry.y2 + boundedDy,
    };
  }
  return {
    cx: clamp01(geometry.cx + dx),
    cy: clamp01(geometry.cy + dy),
    r: geometry.r,
  };
}

/** Traço fino na escala natural (~2px), mesmo critério do ContestedMarkerOverlay. */
export function annotationStrokePx(natural: ImageSize): number {
  return Math.max(1.5, Math.round(Math.max(natural.width, natural.height) * 0.0025));
}

/** Comprimento da ponta da seta em px da imagem, proporcional ao material. */
export function arrowHeadLenPx(natural: ImageSize): number {
  return Math.max(10, Math.round(Math.max(natural.width, natural.height) * 0.025));
}

/** Ponto em px da imagem (não normalizado). */
export interface ImagePoint {
  x: number;
  y: number;
}

/**
 * Triângulo da ponta da seta (px da imagem): [ponta, asa esquerda, asa direita].
 * `len` é o comprimento da ponta em px da imagem.
 */
export function arrowHeadTriangle(
  geometry: ArrowGeometryData,
  natural: ImageSize,
  len: number,
): [ImagePoint, ImagePoint, ImagePoint] {
  const x1 = geometry.x1 * natural.width;
  const y1 = geometry.y1 * natural.height;
  const x2 = geometry.x2 * natural.width;
  const y2 = geometry.y2 * natural.height;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const spread = Math.PI / 7;
  return [
    { x: x2, y: y2 },
    { x: x2 - len * Math.cos(angle - spread), y: y2 - len * Math.sin(angle - spread) },
    { x: x2 - len * Math.cos(angle + spread), y: y2 - len * Math.sin(angle + spread) },
  ];
}

/**
 * Pontos do triângulo da ponta da seta como atributo `points` de SVG.
 * `len` é o comprimento da ponta em px da imagem.
 */
export function arrowHeadPoints(
  geometry: ArrowGeometryData,
  natural: ImageSize,
  len: number,
): string {
  const fmt = (v: number): number => Math.round(v * 100) / 100;
  return arrowHeadTriangle(geometry, natural, len)
    .map((p) => `${fmt(p.x)},${fmt(p.y)}`)
    .join(' ');
}

/**
 * Pose da contestada na mesa "Sobrepor & alinhar" (E36.F3.T03): replica o
 * transform do objeto Fabric com origem no canto superior esquerdo —
 * `canvas = translate(left, top) ∘ rotate(angle) ∘ scale(scaleX, scaleY)`.
 * Mantida como matemática pura para as anotações acompanharem
 * mover/escalar/rotacionar sem depender de chamadas do Fabric (testável no jsdom).
 */
export interface ContestedPose {
  left: number;
  top: number;
  scaleX: number;
  scaleY: number;
  /** Graus, sentido horário (convenção do Fabric). */
  angle: number;
}

/** Ponto em px da IMAGEM contestada → coordenadas do canvas da mesa. */
export function imagePxToCanvas(point: ImagePoint, pose: ContestedPose): ImagePoint {
  const rad = (pose.angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const lx = point.x * pose.scaleX;
  const ly = point.y * pose.scaleY;
  return {
    x: pose.left + lx * cos - ly * sin,
    y: pose.top + lx * sin + ly * cos,
  };
}

/** Geometria de uma anotação em coordenadas do canvas da mesa. */
export type OverlapShape =
  | {
      kind: 'arrow';
      shaft: [ImagePoint, ImagePoint];
      head: [ImagePoint, ImagePoint, ImagePoint];
      strokeWidth: number;
    }
  | { kind: 'circle'; cx: number; cy: number; r: number; strokeWidth: number };

/**
 * Converte a geometria normalizada de uma anotação para o canvas da mesa,
 * ancorada à pose corrente da contestada. O raio do círculo escala por
 * `scaleX` (a contestada escala uniformemente pelos handles de canto — o
 * mesmo pressuposto do `readContested` do OverlapCanvas).
 */
export function overlapShapeFor(
  annotation: { tool: 'arrow' | 'circle'; geometry: AnnotationGeometryData },
  natural: ImageSize,
  pose: ContestedPose,
): OverlapShape {
  const strokeWidth = annotationStrokePx(natural) * pose.scaleX;
  if (annotation.tool === 'arrow' && isArrowGeometry(annotation.geometry)) {
    const g = annotation.geometry;
    const head = arrowHeadTriangle(g, natural, arrowHeadLenPx(natural)).map((p) =>
      imagePxToCanvas(p, pose),
    ) as [ImagePoint, ImagePoint, ImagePoint];
    return {
      kind: 'arrow',
      shaft: [
        imagePxToCanvas({ x: g.x1 * natural.width, y: g.y1 * natural.height }, pose),
        imagePxToCanvas({ x: g.x2 * natural.width, y: g.y2 * natural.height }, pose),
      ],
      head,
      strokeWidth,
    };
  }
  const g = annotation.geometry as CircleGeometryData;
  const center = imagePxToCanvas({ x: g.cx * natural.width, y: g.cy * natural.height }, pose);
  return {
    kind: 'circle',
    cx: center.x,
    cy: center.y,
    r: g.r * natural.width * pose.scaleX,
    strokeWidth,
  };
}
